import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    ElementRef,
    Input,
    OnInit,
    signal,
} from "@angular/core";
import { TypeManagerDecorator } from "../../main/type.map.service";
import { S25ItemI } from "../../pojo/S25ItemI";
import { RelationshipType, SpaceService } from "../../services/space.service";
import { S25Util } from "../../util/s25-util";
import { Item } from "../../pojo/Item";
import { DropDownItem } from "../../pojo/DropDownItem";
import { S25LoadingApi } from "../s25-loading/loading.api";
import { ResourceMicroService } from "../../services/resource.micro.service";
import { StateService } from "../../services/state.service";
import { S25qlService } from "../s25-ql/s25ql.service";
import { Search } from "../s25-ql/s25.home.search.component";

@TypeManagerDecorator("s25-ng-object-details-relationships")
@Component({
    selector: "s25-ng-object-details-relationships",
    template: `
        @if (!relationships.length) {
            none
        }
        <div class="obj-container c-margin-bottom--half">
            @for (rel of relationships; track $index) {
                @let relationshipType = rel[0];
                @let relTypeProp = rel[1];
                @let assocObjects = rel[2];
                @if ($index !== 0) {
                    <div class="obj-divider c-margin-top--half"></div>
                }
                <label class="c-margin-top--quarter">
                    <span class="type-label">{{ relationshipType }}</span>
                    @for (obj of assocObjects; track obj.itemId) {
                        <div class="obj-wrapper">
                            @if (canEdit) {
                                <s25-ng-icon
                                    [type]="'close'"
                                    (click)="deleteItem(+obj.itemId, relTypeProp)"
                                    (keydown.enter)="deleteItem(+obj.itemId, relTypeProp)"
                                    role="button"
                                    tabindex="0"
                                ></s25-ng-icon>
                            }
                            <s25-item-generic
                                [includeTypeIcon]="true"
                                [modelBean]="{ itemTypeId: itemTypeId, itemId: +obj.itemId, itemName: obj.itemName }"
                                [hideLabel]="true"
                            ></s25-item-generic>
                        </div>
                    }
                    @if (relTypes()[relTypeProp]?.showDropdown) {
                        <s25-ng-dropdown-multi-search-criteria
                            [type]="objType"
                            [staticItems]="[{ itemId: 0, itemName: '', favorite: 'T' }]"
                            (done)="addObjects($event, relTypeProp)"
                        ></s25-ng-dropdown-multi-search-criteria>
                    }
                </label>
            }
        </div>
        @if (objType === "events" && showSearchLink) {
            <button class="c-textButton" (click)="navToSearch()">View as Search Results</button>
        }
        <s25-loading-inline [model]="{ text: 'Saving...' }"></s25-loading-inline>
        @if (canEdit && objType !== "events") {
            <label class="c-margin-top--quarter add-type-label">
                <span class="type-label">Add Relationship</span>
                <s25-generic-dropdown
                    [items]="typeOptions[objType]"
                    (chosenChange)="toggleObjDropdown($event)"
                ></s25-generic-dropdown>
            </label>
        }
    `,
    host: {
        "[class.event-relationships]": "objType === 'events'",
    },
    styles: `
        :host {
            display: block;
            padding: 1rem;

            .obj-container {
                display: flex;
                flex-wrap: wrap;
                gap: 2%;

                .obj-divider {
                    border-left: 1px solid rgba(0, 0, 0, 0.15);
                }

                label {
                    display: flex;
                    flex-direction: column;
                    gap: 5px;
                    width: fit-content;

                    .obj-wrapper {
                        display: flex;
                    }
                }
            }

            .add-type-label {
                width: 100%;
            }

            .type-label {
                font-weight: 600;
            }

            &.event-relationships {
                display: flex;

                .obj-container {
                    flex: 1 1 auto;
                }

                .c-textButton {
                    align-self: flex-start;
                }
            }
        }
    `,
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false,
})
export class S25ObjectDetailsRelationshipsComponent implements OnInit {
    @Input({ required: true }) itemId: number;
    @Input({ required: true }) itemTypeId: number;
    @Input({ required: true }) canEdit: boolean;
    @Input({ required: true }) relationships: [string, RelationshipType, S25ItemI[]][];
    @Input({ required: true }) objType: "locations" | "resources" | "events";
    @Input() eventName?: string;
    @Input() showSearchLink?: boolean;

    constructor(
        private cd: ChangeDetectorRef,
        private el: ElementRef,
        private searchService: S25qlService,
    ) {}

    relTypes = signal<RelMap>({} as RelMap);
    typeOptions = {
        locations: [
            { itemName: "Also Assign", itemId: "alsoAssign" },
            { itemName: "Blocked By", itemId: "blockedBy" },
            { itemName: "Close To", itemId: "closeTo" },
            { itemName: "Divides Into", itemId: "dividesInto" },
            { itemName: "Subdivision Of", itemId: "subdivisionOf" },
        ],
        resources: [
            { itemName: "Also Assign", itemId: "alsoAssign" },
            { itemName: "Substitute With", itemId: "substituteWith" },
        ],
    };
    objectIdProp: "spaceId" | "resourceId";
    objectIdLists: Record<RelationshipType, Set<number>> = {} as Record<RelationshipType, Set<number>>; // keep track of object ids so duplicates aren't added

    ngOnInit() {
        this.relationships.forEach(([, relType, assocObjects]) => {
            this.objectIdLists[relType] = new Set([...assocObjects.map((obj) => +obj.itemId)]);
            this.relTypes.update((prev) => ({ ...prev, [relType]: { showDropdown: false } }));
        });
        this.objectIdProp = this.itemTypeId === Item.Ids.Location ? "spaceId" : "resourceId";
    }

    async deleteItem(itemId: number, typeProp: RelationshipType) {
        S25LoadingApi.init(this.el.nativeElement);
        let updateMethod;
        const payload = {
            id: this.itemId,
            remove: {
                relationships: {
                    [typeProp]: [{ [this.objectIdProp]: itemId }],
                },
            },
        };
        switch (this.objType) {
            case "locations": {
                updateMethod = SpaceService.microUpdate(payload);
                break;
            }
            case "resources": {
                updateMethod = ResourceMicroService.saveResource(payload as any);
                break;
            }
        }
        const [_, error] = await S25Util.Maybe(updateMethod);
        S25LoadingApi.destroy(this.el.nativeElement);
        if (error) return console.error(error);
        this.objectIdLists[typeProp]?.has(itemId) && this.objectIdLists[typeProp].delete(itemId);
        this.relationships = this.relationships
            .map((rel) => {
                // filter out object being deleted from relationship type list
                const [relName, relType, assocObjects] = rel;
                if (relType === typeProp) {
                    const updatedObjects = assocObjects.filter((obj) => obj.itemId !== itemId);
                    !updatedObjects.length &&
                        this.relTypes.update((prev) => ({
                            ...prev,
                            [relType]: null,
                        }));
                    return [relName, relType, updatedObjects];
                }
                return rel;
            })
            .filter((rel) => {
                const [, , assocObjects] = rel;
                return !!assocObjects.length;
            }) as [string, RelationshipType, S25ItemI[]][];
        this.cd.markForCheck();
    }

    toggleObjDropdown({ itemId, itemName }: DropDownItem) {
        this.relTypes.update((prev) => {
            const type = itemId as RelationshipType;
            const newObj = {} as RelMap;
            for (const relName in prev) {
                //hide other type dropdowns when one is added for cleaner UI
                const relType = relName as RelationshipType;
                if (prev[relType])
                    newObj[relType] = {
                        showDropdown: relType === type ? !prev[relType].showDropdown : relType === type,
                    };
            }
            this.relationships = this.relationships.filter((rel) => {
                const [, relKey, assocObjects] = rel;
                const hasObjects = !!assocObjects.length;
                if (!hasObjects && prev[relKey]) newObj[relKey] = null;
                return hasObjects;
            }); //if new type added but no objects added, remove when adding new type
            if (!prev[type]) {
                // if the type doesn't exist, add it
                this.relationships.push([itemName, type, []]);
                return { ...newObj, [type]: { showDropdown: true } };
            }
            return newObj;
        });
    }

    async addObjects(objects: DropDownItem[], relType: RelationshipType) {
        objects = objects.filter((obj) => !this.objectIdLists[relType]?.has(+obj.itemId));
        if (!objects?.length) {
            this.relTypes.update((prev) => ({ ...prev, [relType]: { showDropdown: false } }));
            return;
        }
        S25LoadingApi.init(this.el.nativeElement);
        const payload = {
            id: this.itemId,
            relationships: {
                [relType]: objects.map((obj) => ({ [this.objectIdProp]: obj.itemId })),
            },
        };
        let microUpdate;
        switch (this.objType) {
            case "locations": {
                microUpdate = SpaceService.microUpdate(payload);
                break;
            }
            case "resources": {
                microUpdate = ResourceMicroService.saveResource(payload);
                break;
            }
        }
        const [_, error] = await S25Util.Maybe(microUpdate);
        S25LoadingApi.destroy(this.el.nativeElement);
        if (error) return console.error(error);
        this.relTypes.update((prev) => ({ ...prev, [relType]: { showDropdown: false } }));
        this.objectIdLists[relType] = new Set([
            ...(this.objectIdLists?.[relType] ?? []),
            ...objects.map((obj) => +obj.itemId),
        ]);
        this.relationships = this.relationships.map((rel) => {
            const [relName, relTypeProp, assocObjects] = rel;
            if (relTypeProp === relType) {
                // add new objects to the existing relationship type
                const newObjects = assocObjects.concat(objects.map((obj) => ({ itemId: obj.itemId })));
                return [relName, relTypeProp, newObjects];
            }
            return rel;
        });
        this.cd.markForCheck();
    }

    async navToSearch() {
        await StateService.go(
            "search",
            { viz: "list", subject: "event", search: "" },
            { source: "Event Details - Relationships - View as search results" },
        );
        this.searchService.triggerSearch({
            detail: {
                viz: "list",
                subject: "event",
                search: "",
                advanced: {
                    model: {
                        query_method: "all",
                        step: [
                            {
                                step_type_id: 107,
                                step_param: [{ itemId: this.itemId, itemName: this.eventName ?? "" }],
                            },
                            {
                                step_type_id: 20,
                                sub_step_type_id: 1,
                                step_param: [{ related_event_type_id: 11 }],
                            },
                        ],
                    },
                    searches: {},
                },
            },
        } as Search.Params);
    }
}

type RelMap = Record<RelationshipType, { showDropdown: boolean }>;
